import React, { Fragment } from 'react';

import { SharedButtonProps } from '../../types/SharedButtonProps';
import { UserAction } from '../../types/UserAction';
import { Box } from '../Box/Box';
import { Button } from '../Button/Button';
import { Divider } from '../Divider/Divider';
import { Menu } from '../Menu/Menu';

type MenuConfig = Omit<
  React.ComponentProps<typeof Menu>,
  'trigger' | 'disabled'
>;

export interface MenuAction extends Pick<
  UserAction,
  | 'text'
  | 'icon'
  | 'iconPosition'
  | 'disabled'
  | 'tooltip'
  | 'level'
  | 'mode'
  | 'status'
> {
  menu: MenuConfig;
}

export type ButtonGroupAction = UserAction | MenuAction;

const isMenuAction = (action: ButtonGroupAction): action is MenuAction =>
  'menu' in action;

type Mode = 'pill' | 'chip' | 'action';

interface Props {
  /** Button and/or menu actions */
  actions: ButtonGroupAction[];
  /** Disables the entire button group */
  disabled?: boolean;
  /** Stretches the width of the button to its parent container */
  isFullWidth?: boolean;
  /** UI modes */
  mode?: Mode;
  size?: SharedButtonProps['size'];
}

export const ButtonGroup = ({
  actions,
  disabled: allDisabled = false,
  isFullWidth,
  mode: groupMode,
  size,
}: Props) => {
  return (
    <Box display="flex" role="group">
      {actions.map((action, index) => {
        const {
          disabled,
          icon,
          iconPosition,
          mode,
          status,
          text,
          tooltip,
          level,
        } = action;
        const isActive = status === 'active';
        const isFirst = index === 0;
        const isLast = index === actions.length - 1;
        const isPill = groupMode === 'pill';
        // TODO: use formal chip mode button API
        const isChip = groupMode === 'chip';
        const isAction = groupMode === 'action';

        const showDivider = isAction && !isLast;
        const buttonElement = (
          <Button
            aria-pressed={Boolean(status)}
            disabled={allDisabled || disabled}
            icon={icon}
            iconPosition={iconPosition}
            isFullWidth={isFullWidth}
            mode={mode}
            status={status}
            text={text}
            tooltip={tooltip}
            size={size}
            onClick={isMenuAction(action) ? undefined : action.onClick}
            variant={level}
          />
        );
        return (
          <Fragment key={index}>
            <Box
              flex={isFullWidth ? 'auto' : 'none'}
              alignItems="center"
              styles={[
                isAction ? {} : componentStyles.button,
                isLast && !isAction ? componentStyles.last({ isPill }) : {},
                isFirst && !isAction ? componentStyles.first({ isPill }) : {},
                isActive && !status ? componentStyles.active : {},
                isChip ? componentStyles.chip : {},
                isAction ? componentStyles.action : {},
              ]}
            >
              {isMenuAction(action) ? (
                <Menu
                  {...action.menu}
                  disabled={allDisabled || disabled}
                  trigger={buttonElement}
                />
              ) : (
                buttonElement
              )}
            </Box>
            {showDivider && <Divider isHorizontal={false} height="s" />}
          </Fragment>
        );
      })}
    </Box>
  );
};

const componentStyles = {
  button: {
    '& button': {
      border: 'border',
      borderLeftColor: 'transparent',
      borderRadius: 0,
    },
  },
  chip: {
    '& button': {
      borderTopLeftRadius: 'l',
      borderBottomLeftRadius: 'l',
      borderTopRightRadius: 'l',
      borderBottomRightRadius: 'l',
      marginRight: 2,
      borderColor: 'border',
      height: 'auto',
      paddingTop: 1,
      paddingBottom: 1,
    },
  },
  first: ({ isPill }: { isPill: boolean }) => {
    return {
      '& button': {
        borderLeftColor: 'border',
        borderBottomLeftRadius: isPill ? 'pill' : 'm',
        borderTopLeftRadius: isPill ? 'pill' : 'm',
      },
    };
  },
  // TODO: drive the logic purely via `Button.status` (discuss with design to prefer `Button[status="active"]`)
  active: {
    '& button': {
      backgroundColor: 'background.quiet',
    },
  },
  last: ({ isPill }: { isPill: boolean }) => {
    return {
      '& button': {
        borderBottomRightRadius: isPill ? 'pill' : 'm',
        borderTopRightRadius: isPill ? 'pill' : 'm',
      },
    };
  },
  action: {
    '& button': {
      border: 'none',
      borderRadius: 'm',
    },
  },
};
